/* eslint-disable no-unused-vars */
import { UpdateProfileStatus, useCustomerDetails, useUpdateCustomerAddress } from '@egw/api'
import { Button, ButtonVariant, Input, Select } from '@egw/components'
import {
  CATEGORY_CORE,
  CUSTOMER_SERVICE_PHONE_NUMBER,
  CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY,
  STATES_OPTIONS,
  SUBCATEGORY_PROFILE,
} from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { type SmartyStreetAddressFormat } from '../../types'
import {
  convertRecommendedToAddressInput,
  createAddressLines,
  getSmartyRecommendation,
  isSecondaryInfoRequired,
} from '../../utils'

export enum UpdateAddressStepEnum {
  CHANGE = 'CHANGE_ADDRESS',
  VERIFY = 'VERIFY_ADDRESS',
  CONFIRM = 'CONFIRM_ADDRESS',
  ERROR = 'ERROR_ADDRESS',
}

export enum UpdateAddressField {
  AddressLine1 = 'addressLine1',
  AddressLine2 = 'addressLine2',
  AddressCity = 'addressCity',
  AddressPostalCode = 'addressPostalCode',
  AddressRegionCode = 'addressRegionCode',
}

const UpdateAddress: FC<{ onClose: () => void; updateModalTitle: Dispatch<SetStateAction<string>> }> = ({
  onClose,
  updateModalTitle,
}) => {
  // Hooks
  const { t } = useTranslation('features-settings')
  const translationKeyPrefix = 'update_address.'
  const { refetch: refetchSettings } = useCustomerDetails()

  // State
  const [step, setStep] = useState<UpdateAddressStepEnum>(UpdateAddressStepEnum.CHANGE)

  const [addressError, setAddressError] = useState<boolean>(false)

  const [recommendedAddress, setRecommendedAddress] = useState<SmartyStreetAddressFormat | null>(null)

  // Effects
  // Data Layer Events
  useEffect(() => {
    if (step === UpdateAddressStepEnum.CONFIRM) {
      sendEvent('user_addressVerified_visit', {
        category: CATEGORY_CORE,
        subCategory: SUBCATEGORY_PROFILE,
        addressLine1: recommendedAddress?.addressLine1,
        addressLine2: recommendedAddress?.addressLine2,
        city: recommendedAddress?.addressCity,
        state: recommendedAddress?.addressRegionCode,
        zip: recommendedAddress?.addressPostalCode,
        dateUpdated: new Date().toUTCString(),
      })
    }
  }, [step, recommendedAddress])

  const ClientServiceError = useMemo(
    () => (
      <div className="rounded-md bg-alertErrorBg p-4">
        <p className="text-sm text-alertErrorText">
          {t('client_service.update_error')}
          <a
            href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
            title={t('client_service.call')}
            rel="noopener"
            className="text-sky-500 hover:underline font-bold"
          >
            {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
          </a>
        </p>
      </div>
    ),
    [t],
  )

  const updateStep = useCallback(
    (newStep: UpdateAddressStepEnum): void => {
      setStep(newStep)
      updateModalTitle(t(`update_address.${newStep}.modal_title`))
    },
    [setStep, updateModalTitle, t],
  )

  const { mutate: updateAddress, isLoading: isUpdateAddressLoading } = useUpdateCustomerAddress({
    onSuccess: (data) => {
      if (data.__typename === 'UpdateProfileSuccess') {
        if (data.status === UpdateProfileStatus.COMPLETE) {
          refetchSettings().catch((e: unknown) => {
            console.error(e)
          })
          updateStep(UpdateAddressStepEnum.CONFIRM)
        }
        if (data.status === UpdateProfileStatus.FAILED) {
          setAddressError(true)
        }
      }
      if (data.__typename === 'UpdateProfileError') {
        setAddressError(true)
      }
    },
    onError: () => {
      setAddressError(true)
    },
  })

  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formState: { errors: formErrors, isDirty, isValid },
  } = useForm({
    defaultValues: {
      [UpdateAddressField.AddressLine1]: '',
      [UpdateAddressField.AddressLine2]: '',
      [UpdateAddressField.AddressCity]: '',
      [UpdateAddressField.AddressPostalCode]: '',
      [UpdateAddressField.AddressRegionCode]: '',
    } as {
      [key in UpdateAddressField]: string
    },
    resolver: yupResolver(
      yup.object().shape({
        [UpdateAddressField.AddressLine1]: yup.string().required(t('update_address.CHANGE_ADDRESS.required_error')),
        [UpdateAddressField.AddressLine2]: yup.string().notRequired(),
        [UpdateAddressField.AddressCity]: yup.string().required(t('update_address.CHANGE_ADDRESS.required_error')),
        [UpdateAddressField.AddressRegionCode]: yup
          .string()
          .required(t('update_address.CHANGE_ADDRESS.required_error')),
        [UpdateAddressField.AddressPostalCode]: yup
          .string()
          .required(t('update_address.CHANGE_ADDRESS.required_error')),
      }),
    ),
  })

  const onSubmit = handleSubmit(async (): Promise<void> => {
    setAddressError(false)
    // Verify with smarty streets and offer recommended address
    const recommendedSmartyAddress = await getSmartyRecommendation(getValues())

    // ERROR HANDLING HERE
    // If recommended address found but additional info required
    const addressIssues = recommendedSmartyAddress ? isSecondaryInfoRequired(recommendedSmartyAddress.analysis) : null

    // If Secondary Info is required Inform the user
    if (addressIssues)
      setError(UpdateAddressField.AddressLine2, {
        message: t(`update_address.${step}.secondary_info_error`),
      })

    // If a recommended address is not found set an error in the modal
    if (!recommendedSmartyAddress) setAddressError(true)

    // Happy Path Recommended Address Found
    if (recommendedSmartyAddress && !addressIssues) {
      const formattedAddress = createAddressLines(recommendedSmartyAddress.components)
      setRecommendedAddress(formattedAddress)
      updateStep(UpdateAddressStepEnum.VERIFY)
    }
  })

  const onVerifyChange = useCallback((): void => {
    if (!recommendedAddress) return
    updateAddress({
      address: convertRecommendedToAddressInput(recommendedAddress),
    }).catch((e: unknown) => {
      console.log(e)
    })
  }, [recommendedAddress, updateAddress])

  const onKeepEditing = useCallback(() => {
    updateStep(UpdateAddressStepEnum.CHANGE)
  }, [updateStep])

  interface TDisplayAddress {
    address: SmartyStreetAddressFormat
    textColor?: string
  }

  const DisplayAddress: FC<TDisplayAddress> = ({ address, textColor }) => {
    const { addressLine1, addressLine2, addressCity, addressPostalCode, addressRegionCode } = address

    const fontColor = textColor ?? 'text-gray-700'

    return (
      <div className={`flex flex-col text-base fs-exclude ${fontColor}`}>
        {addressLine1 ? <p>{addressLine1}</p> : null}
        {addressLine2 ? <p>{addressLine2}</p> : null}
        <div className="flex flex-row">
          {addressCity ? <span>{addressCity} &nbsp;</span> : null}
          {addressRegionCode ? <span>{addressRegionCode} &nbsp;</span> : null}
          {addressPostalCode ? <span>{addressPostalCode}</span> : null}
        </div>
      </div>
    )
  }

  const ChangeAddressStep = useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <p>{t(`${translationKeyPrefix}${step}.modal_description`)}</p>
        {addressError ? ClientServiceError : null}
        <form id="updateAddress">
          <div className="flex flex-col gap-4">
            <Controller
              name={UpdateAddressField.AddressLine1}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id={UpdateAddressField.AddressLine1}
                  testId={UpdateAddressField.AddressLine1}
                  label={t(`${translationKeyPrefix}${step}.addressLine1`)}
                  required
                  error={formErrors[UpdateAddressField.AddressLine1]?.message}
                  isPII
                />
              )}
            />
            <Controller
              name={UpdateAddressField.AddressLine2}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id={UpdateAddressField.AddressLine2}
                  testId={UpdateAddressField.AddressLine2}
                  label={t(`${translationKeyPrefix}${step}.addressLine2`)}
                  required={false}
                  error={formErrors[UpdateAddressField.AddressLine2]?.message}
                  value={field.value ?? ''}
                  isPII
                />
              )}
            />
            <Controller
              name={UpdateAddressField.AddressCity}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  id={UpdateAddressField.AddressCity}
                  testId={UpdateAddressField.AddressCity}
                  label={t(`${translationKeyPrefix}${step}.addressCity`)}
                  required
                  error={formErrors[UpdateAddressField.AddressCity]?.message}
                  isPII
                />
              )}
            />
            <div className="flex flex-row gap-4">
              <Controller
                name={UpdateAddressField.AddressRegionCode}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    id={UpdateAddressField.AddressRegionCode}
                    testId={UpdateAddressField.AddressRegionCode}
                    options={STATES_OPTIONS}
                    label={t(`${translationKeyPrefix}${step}.addressRegionCode`)}
                    required
                    error={formErrors[UpdateAddressField.AddressRegionCode]?.message}
                    className="w-full"
                    isPII
                  />
                )}
              />
              <Controller
                name={UpdateAddressField.AddressPostalCode}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    id={UpdateAddressField.AddressPostalCode}
                    testId={UpdateAddressField.AddressPostalCode}
                    label={t(`${translationKeyPrefix}${step}.addressPostalCode`)}
                    required
                    error={formErrors[UpdateAddressField.AddressPostalCode]?.message}
                    isPII
                  />
                )}
              />
            </div>
          </div>
        </form>

        <Button
          id="piChangeContinue"
          className="my-4 w-full"
          isDisabled={Boolean(!isValid)}
          onClick={onSubmit}
          variant={ButtonVariant.Primary}
        >
          {t('settings.personal_info.change.continue')}
        </Button>
      </div>
    )
  }, [addressError, ClientServiceError, control, formErrors, isDirty, isValid, onSubmit, step, t])

  const VerifyAddressStep = useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        {addressError ? ClientServiceError : null}
        <div className="gap-2 flex flex-col mt-4">
          <p className="text-gray-900 font-bold text-lg">{t(`${translationKeyPrefix}${step}.you_entered`)}</p>
          <DisplayAddress address={getValues() as SmartyStreetAddressFormat} />
          <p className="text-gray-900 font-bold text-lg">{t(`${translationKeyPrefix}${step}.we_found`)}</p>
          <DisplayAddress address={recommendedAddress!} />
        </div>
        <div className="flex flex-col gap-2">
          <Button
            isLoading={isUpdateAddressLoading}
            id="useAddressWeFound"
            onClick={onVerifyChange}
            variant={ButtonVariant.Primary}
          >
            {t(`${translationKeyPrefix}${step}.primaryCta`)}
          </Button>
          <Button
            id="keepEditingAddress"
            variant={ButtonVariant.Secondary}
            onClick={onKeepEditing}
            isDisabled={isUpdateAddressLoading}
          >
            {t(`${translationKeyPrefix}${step}.secondaryCta`)}
          </Button>
        </div>
      </div>
    )
  }, [
    addressError,
    ClientServiceError,
    getValues,
    isUpdateAddressLoading,
    onKeepEditing,
    onVerifyChange,
    recommendedAddress,
    step,
    t,
  ])

  const ConfirmAddressStep = useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-row gap-4">
          <CheckCircleIcon className="fill-green-400" height={17} width={17} />
          <DisplayAddress textColor="text-textSuccess items-start" address={recommendedAddress!} />
        </div>
        <Button id="closeModal" variant={ButtonVariant.Primary} onClick={onClose}>
          {t('settings.personal_info.change.done')}
        </Button>
      </div>
    )
  }, [onClose, recommendedAddress, t])

  const content = useMemo(() => {
    return {
      [UpdateAddressStepEnum.CHANGE]: ChangeAddressStep,
      [UpdateAddressStepEnum.VERIFY]: VerifyAddressStep,
      [UpdateAddressStepEnum.CONFIRM]: ConfirmAddressStep,
      [UpdateAddressStepEnum.ERROR]: ClientServiceError,
    }[step]
  }, [ChangeAddressStep, ConfirmAddressStep, VerifyAddressStep, ClientServiceError, step])

  return content
}

export default UpdateAddress
